import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { orderConflictRetryPolicy } from '../../../service/api/conflictRetry';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import type { OrderShipmentPatchBody } from '@vs-shop/schemas/orderShipment';
import type {
  OrderShipmentId,
  ShippingMethodCode,
} from '../../../service/orderShipment/OrderShipment';
import type { PlacementId } from '../../../service/shipping/Placement';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/orders/{tokenValue}/shipments/{shipmentId}';
const HTTP_METHOD = 'patch';
type ShippingPatch = OrderShipmentPatchBody & {
  readonly shippingMethod: ShippingMethodCode;
  readonly placementId: PlacementId | null;
};

export type MakeUpdateShippingInOrderDeps<OShowType> = {
  readonly orderShow: Schema.Codec<OShowType, unknown>;
};

export const makeUpdateShippingInOrder = <OShowType>(
  deps: MakeUpdateShippingInOrderDeps<OShowType>
) => {
  const UpdateShippingError = createBackendApiError({
    domain: 'Order',
    endpoint: 'UpdateShipping',
    path: ENDPOINT,
    identifierField: 'orderToken',
  } as const);

  const patchOrderShippingFetch = (
    tokenValue: string,
    shipmentId: OrderShipmentId,
    data: ShippingPatch
  ) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json', 'application/merge-patch+json')
        .fetch({ params: { tokenValue, shipmentId: String(shipmentId) }, body: data });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'order.token': tokenValue,
          'shipment.id': shipmentId,
          'shipment.method': data.shippingMethod,
          'shipment.placement_id': data.placementId,
        },
      })
    );

  const updateShippingInOrder = (
    tokenValue: string,
    shipmentId: OrderShipmentId,
    data: ShippingPatch
  ) =>
    patchOrderShippingFetch(tokenValue, shipmentId, data).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.orderShow)(response),
          404: (response) =>
            Effect.fail(
              new UpdateShippingError.NotFoundError({ orderToken: tokenValue, response })
            ),
          422: UpdateShippingError.createViolationError(tokenValue),
          // A rejected placement lands here, not in the 422 branch.
          // `ChooseShippingMethodHandler` states its placement rules with
          // `Assert::`, which raises an `InvalidArgumentException` and reaches
          // the client as a 500. Without this the shopper's last checkout step
          // fails on a generic request error naming neither the method nor the
          // id, and the pair is what says which rule was broken.
          orElse: (response) =>
            logApiOperation(
              ApiModule.OrderShippingUpdate,
              'Error',
              `Shipping update rejected with ${response.status} for order ${tokenValue}: method ${data.shippingMethod}, placementId ${JSON.stringify(data.placementId)}`
            ).pipe(
              Effect.andThen(
                Effect.fail(
                  new UpdateShippingError.ApiRequestError({ orderToken: tokenValue, response })
                )
              )
            ),
        })
      ),
      Effect.retry(orderConflictRetryPolicy),
      Effect.tap(() =>
        logApiOperation(
          ApiModule.OrderShippingUpdate,
          'Debug',
          `Successfully updated shipping method to ${data.shippingMethod} for order ${tokenValue}`
        )
      ),
      Effect.withSpan('api.orderShipping.update', {
        kind: 'internal',
        attributes: {
          'order.token': tokenValue,
          'shipment.id': shipmentId,
          'shipment.method': data.shippingMethod,
          'shipment.placement_id': data.placementId,
        },
      })
    );

  return { updateShippingInOrder, UpdateShippingError };
};
